import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import { api } from "../services/apiClient";
import { SubscriptionSection } from "../components/account/SubscriptionSection";
import { ProfileSection } from "../components/account/ProfileSection";
import { PasswordSection } from "../components/account/PasswordSection";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import {
  getDerivedPaymentStatus,
  requiresActivation,
} from "../constants/plans";
import { MICROCOPY } from "../constants/microcopy";
import { useSubscriptionActivation } from "../hooks/useSubscriptionActivation";

const AccountSettings = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(user || {});
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const subscriptionTabOpen = searchParams.get("tab") === "subscription";
  const paymentStatus = getDerivedPaymentStatus(currentUser);
  const canActivateSubscription = requiresActivation(currentUser);
  const { activateSubscription, isActivatingSubscription } =
    useSubscriptionActivation({
      user: currentUser,
      navigate,
    });

  useEffect(() => {
    fetchCurrentUser();
    fetchSubscriptionInfo();
    fetchEntitlements();
  }, []);

  const fetchEntitlements = async () => {
    try {
      const response = await api.get("/entitlements/me");
      if (response.ok) {
        const data = await response.json();
        setEntitlements(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await api.get("/subscription/info");
      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get("/auth/me");
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
        updateUser({ ...user, ...data });
        setFormData({ name: data.name, email: data.email });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.patch("/auth/me", {
        name: formData.name,
        email: formData.email,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Error al actualizar");
      }
      const data = await response.json();
      toast.success(data.message || "Perfil actualizado");
      if (data.user) {
        updateUser({ ...user, ...data.user });
        setCurrentUser((prev) => ({ ...prev, ...data.user }));
        setFormData({ name: data.user.name, email: data.user.email });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    setPasswordLoading(true);
    try {
      const response = await api.patch("/auth/me/password", {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Error al cambiar contraseña");
      }
      const data = await response.json();
      toast.success(data.message);
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleActivateSubscription = () => activateSubscription();

  const handleManageBilling = async () => {
    try {
      const origin = window.location.origin;
      const response = await api.post("/subscription/portal", {
        origin_url: origin,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.detail || "No se pudo abrir el portal de facturación",
        );
      }
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      toast.error(error.message || "Error al abrir portal de Stripe");
    }
  };

  const hasProfileChanges =
    formData.name !== currentUser?.name ||
    formData.email !== currentUser?.email;
  const hasPasswordData =
    passwordData.current_password &&
    passwordData.new_password &&
    passwordData.confirm_password;

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1
          className="text-3xl font-bold mb-8"
          data-testid="account-settings-title"
        >
          Mi Cuenta
        </h1>
        <div className="bg-background border border-border rounded-lg p-5 mb-6">
          <p className="text-sm font-semibold">{MICROCOPY.account.headline}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {MICROCOPY.account.subheadline}
          </p>
        </div>

        <SubscriptionSection
          currentUser={currentUser}
          subscriptionInfo={subscriptionInfo}
          entitlements={entitlements}
          paymentStatus={paymentStatus}
          canActivateSubscription={canActivateSubscription}
          isActivatingSubscription={isActivatingSubscription}
          handleActivateSubscription={handleActivateSubscription}
          handleManageBilling={handleManageBilling}
          navigate={navigate}
          subscriptionTabOpen={subscriptionTabOpen}
        />

        <ProfileSection
          formData={formData}
          setFormData={setFormData}
          currentUser={currentUser}
          loading={loading}
          hasProfileChanges={hasProfileChanges}
          handleSubmit={handleSubmit}
        />

        <PasswordSection
          passwordData={passwordData}
          setPasswordData={setPasswordData}
          handlePasswordChange={handlePasswordChange}
          passwordLoading={passwordLoading}
          hasPasswordData={hasPasswordData}
        />

        <div className="flex flex-col sm:flex-row gap-3">
          <BackNavigationButton dataTestId="back-button" />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccountSettings;
